import { MetadataKeyFilterInterface, SelectedMetadataInterface } from "../../interfaces/ElasticSearchInterface";
import { createRegexQuery, createTermsQuery, emptyDataObject, SourceInfo, TitleTargetAndSource } from "../info";

import { AGGREGATION_SIZE } from "@me8eon/config";

export const languagesMap: Record<string, string> = {
    "aa": "Afar",
    "ab": "Abkhazian",
    "ae": "Avestan",
    "af": "Afrikaans",
    "ak": "Akan",
    "am": "Amharic",
    "an": "Aragonese",
    "ar": "Arabic",
    "as": "Assamese",
    "av": "Avaric",
    "ay": "Aymara",
    "az": "Azerbaijani",
    "ba": "Bashkir",
    "be": "Belarusian",
    "bg": "Bulgarian",
    "bh": "Bihari",
    "bi": "Bislama",
    "bm": "Bambara",
    "bn": "Bengali",
    "bo": "Tibetan",
    "br": "Breton",
    "bs": "Bosnian",
    "ca": "Catalan",
    "ce": "Chechen",
    "ch": "Chamorro",
    "co": "Corsican",
    "cr": "Cree",
    "cs": "Czech",
    "cu": "Church Slavic",
    "cv": "Chuvash",
    "cy": "Welsh",
    "da": "Danish",
    "de": "German",
    "dv": "Divehi",
    "dz": "Dzongkha",
    "ee": "Ewe",
    "el": "Greek",
    "en": "English",
    "eo": "Esperanto",
    "es": "Spanish",
    "et": "Estonian",
    "eu": "Basque",
    "fa": "Persian",
    "ff": "Fulah",
    "fi": "Finnish",
    "fj": "Fijian",
    "fo": "Faroese",
    "fr": "French",
    "fy": "Western Frisian",
    "ga": "Irish",
    "gd": "Scottish Gaelic",
    "gl": "Galician",
    "gn": "Guarani",
    "gu": "Gujarati",
    "gv": "Manx",
    "ha": "Hausa",
    "he": "Hebrew",
    "hi": "Hindi",
    "ho": "Hiri Motu",
    "hr": "Croatian",
    "ht": "Haitian",
    "hu": "Hungarian",
    "hy": "Armenian",
    "hz": "Herero",
    "ia": "Interlingua",
    "id": "Indonesian",
    "ie": "Interlingue",
    "ig": "Igbo",
    "ii": "Sichuan Yi",
    "ik": "Inupiaq",
    "io": "Ido",
    "is": "Icelandic",
    "it": "Italian",
    "iu": "Inuktitut",
    "ja": "Japanese",
    "jv": "Javanese",
    "ka": "Georgian",
    "kg": "Kongo",
    "ki": "Kikuyu",
    "kj": "Kuanyama",
    "kk": "Kazakh",
    "kl": "Kalaallisut",
    "km": "Khmer",
    "kn": "Kannada",
    "ko": "Korean",
    "kr": "Kanuri",
    "ks": "Kashmiri",
    "ku": "Kurdish",
    "kv": "Komi",
    "kw": "Cornish",
    "ky": "Kirghiz",
    "la": "Latin",
    "lb": "Luxembourgish",
    "lg": "Ganda",
    "li": "Limburgan",
    "ln": "Lingala",
    "lo": "Lao",
    "lt": "Lithuanian",
    "lu": "Luba-Katanga",
    "lv": "Latvian",
    "mg": "Malagasy",
    "mh": "Marshallese",
    "mi": "Maori",
    "mk": "Macedonian",
    "ml": "Malayalam",
    "mn": "Mongolian",
    "mr": "Marathi",
    "ms": "Malay",
    "mt": "Maltese",
    "my": "Burmese",
    "na": "Nauru",
    "nb": "Norwegian Bokmål",
    "nd": "North Ndebele",
    "ne": "Nepali",
    "ng": "Ndonga",
    "nl": "Dutch",
    "nn": "Norwegian Nynorsk",
    "no": "Norwegian",
    "nr": "South Ndebele",
    "nv": "Navajo",
    "ny": "Chichewa",
    "oc": "Occitan",
    "oj": "Ojibwa",
    "om": "Oromo",
    "or": "Oriya",
    "os": "Ossetian",
    "pa": "Punjabi",
    "pi": "Pali",
    "pl": "Polish",
    "ps": "Pashto",
    "pt": "Portuguese",
    "qu": "Quechua",
    "rm": "Romansh",
    "rn": "Rundi",
    "ro": "Romanian",
    "ru": "Russian",
    "rw": "Kinyarwanda",
    "sa": "Sanskrit",
    "sc": "Sardinian",
    "sd": "Sindhi",
    "se": "Northern Sami",
    "sg": "Sango",
    "si": "Sinhala",
    "sk": "Slovak",
    "sl": "Slovenian",
    "sm": "Samoan",
    "sn": "Shona",
    "so": "Somali",
    "sq": "Albanian",
    "sr": "Serbian",
    "ss": "Swati",
    "st": "Southern Sotho",
    "su": "Sundanese",
    "sv": "Swedish",
    "sw": "Swahili",
    "ta": "Tamil",
    "te": "Telugu",
    "tg": "Tajik",
    "th": "Thai",
    "ti": "Tigrinya",
    "tk": "Turkmen",
    "tl": "Tagalog",
    "tn": "Tswana",
    "to": "Tonga",
    "tr": "Turkish",
    "ts": "Tsonga",
    "tt": "Tatar",
    "tw": "Twi",
    "ty": "Tahitian",
    "ug": "Uighur",
    "uk": "Ukrainian",
    "ur": "Urdu",
    "uz": "Uzbek",
    "ve": "Venda",
    "vi": "Vietnamese",
    "vo": "Volapük",
    "wa": "Walloon",
    "wo": "Wolof",
    "xh": "Xhosa",
    "yi": "Yiddish",
    "yo": "Yoruba",
    "za": "Zhuang",
    "zh": "Chinese",
    "zu": "Zulu"
};

function getLanguages(languages?: string) {
    if (languages) {
        const langArray = languages.split(",");
        if (langArray.length > 2) {
            const langString = langArray
                .slice(0, 2)
                .map((lang) => languagesMap[lang] ?? lang.toUpperCase())
                .join(", ");
            return `${langString} & ${langArray.length - 2} more`;
        } else {
            return langArray.map((lang) => languagesMap[lang] ?? lang.toUpperCase()).join(", ");
        }
    } else {
        return null;
    }
}

function getDuration(duration: number): string {
    duration = duration * 86400;

    const days = Math.floor(duration / 86400); // 86400 seconds in a day
    const hours = Math.floor((duration % 86400) / 3600);
    const minutes = Math.floor((duration % 3600) / 60);
    // const remainingSeconds = duration % 60;

    const result = [];

    if (days > 0) {
        result.push(`${days} ${days > 1 ? "days" : "day"}`);
    }
    if (hours > 0) {
        result.push(`${hours} ${hours > 1 ? "hours" : "hour"}`);
    }
    if (minutes > 0) {
        result.push(`${minutes} ${minutes > 1 ? "minutes" : "minute"}`);
    }
    // if (remainingSeconds > 0) {
    //     result.push(
    //         `${remainingSeconds} ${remainingSeconds > 1 ? "seconds" : "second"}`
    //     );
    // }

    return result.join(" ");
}

const index = "mygenius-api";

export const myGeniusInfo: SourceInfo = {
    match: "mygenius",
    dropDown: {
        label: "MyGenius",
        value: index,
        icon: "mygenius",
    },
    makeDataObject: (result) => ({
        ...emptyDataObject,
        id: result?.ecl_id,
        index: result?.index,
        icon: "mygenius",
        link: `https://eon.edcast.com/insights/ECL-${result.ecl_id}`,
        name: result?.card_title,
        source: `MyGenius - [${result?.ecl_source_name}]`,
        type: result?.standard_card_type,
        desc: result?.card_description,
        duration: getDuration(result?.Duration_HHMM),
        language: getLanguages(result?.card_languages),
        render: "markdown",
    }),
    titleAndTarget: function (): TitleTargetAndSource {
        throw new Error("Function not implemented.");
    },
    refineFilterLabels: (key: string, value: any) => {
        switch (key) {
        case "card_languages": {
            const uniqueLanguages: { [key: string]: MetadataKeyFilterInterface } = {};
            value.forEach((obj: MetadataKeyFilterInterface) => {
                obj.key.split(',').forEach((key: string) => {
                    if (!uniqueLanguages[key]) {
                        uniqueLanguages[key] = { key: key, doc_count: obj.doc_count, label: languagesMap[key] ?? key.toUpperCase() };
                    } else {
                        uniqueLanguages[key].doc_count += obj.doc_count;
                    }
                });
            });
            const uniqueLanguagesArray: MetadataKeyFilterInterface[] = Object.values(uniqueLanguages);
            return { label: "Languages", value: uniqueLanguagesArray };
        }
        case "ecl_source_name": {
            return { label: "Source", value };
        }
        case "standard_card_type": {
            return { label: "Type", value };
        }
        default:
            return { label: key, value };
        }
    },
    refineFiltersAggregationQuery: {
        [index]: {
            filter: {
                term: {
                    _index: index,
                },
            },
            aggs: {
                card_languages: {
                    terms: {
                        field: "card_languages.keyword",
                        size: AGGREGATION_SIZE
                    },
                },
                ecl_source_name: {
                    terms: {
                        field: "ecl_source_name.keyword",
                        size: AGGREGATION_SIZE
                    },
                },
                standard_card_type: {
                    terms: {
                        field: "standard_card_type.keyword",
                        size: AGGREGATION_SIZE
                    },
                },
            },
        }
    },
    createMetadataFilters: (selectedMetadata: SelectedMetadataInterface): any => {
        const searchQuery = [];
        for (const key in selectedMetadata) {
            if (!selectedMetadata[key].length) continue;
            switch (key) {
            case "card_languages": {
                const regexQuery = createRegexQuery(selectedMetadata, key);
                regexQuery && searchQuery.push(regexQuery);
                break;
            }
            case "ecl_source_name":
            case "standard_card_type": {
                const termsQuery = createTermsQuery(selectedMetadata, key);
                termsQuery && searchQuery.push(termsQuery);
                break;
            }
            default:
                return [];
            }
        }
        return searchQuery;
    },
};
